import { z } from 'zod';

/** Qualitative flags only — never invented probabilities (spec §7.3). */
export const ConfidenceSchema = z.enum(['high', 'medium', 'uncertain']);
export type Confidence = z.infer<typeof ConfidenceSchema>;

export const TranscriptionLineSchema = z.object({
  text: z.string(),
  confidence: ConfidenceSchema,
  /** Normalised [x, y, w, h] in 0..1 page space if the provider returned one; usually null. */
  region: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable().default(null),
});

export const TranscriptionPageSchema = z.object({
  page: z.number().int().min(1),
  text: z.string(),
  lines: z.array(TranscriptionLineSchema).default([]),
});

export const UncertainSpanSchema = z.object({
  start: z.number().int().min(0),
  end: z.number().int().min(0),
  reason: z.string().max(300),
});
export type UncertainSpan = z.infer<typeof UncertainSpanSchema>;

export const TranscriptionResultSchema = z.object({
  pages: z.array(TranscriptionPageSchema).min(1),
  fullText: z.string(),
  uncertainSpans: z.array(UncertainSpanSchema).default([]),
  /** e.g. "crossed-out word on line 3", "illegible line" */
  notes: z.array(z.string().max(500)).default([]),
  /** Set when the pages contain no recognisable handwriting. */
  blank: z.boolean().default(false),
});
export type TranscriptionResult = z.infer<typeof TranscriptionResultSchema>;

export const FREE_ISSUE_CATEGORIES = [
  'accent',
  'spelling',
  'final_sigma',
  'article',
  'agreement',
  'noun_case',
  'verb_form',
  'word_order',
  'punctuation',
  'register',
  'vocabulary',
  'task_coverage',
  'other',
] as const;
export const FreeIssueCategorySchema = z.enum(FREE_ISSUE_CATEGORIES);
export type FreeIssueCategory = z.infer<typeof FreeIssueCategorySchema>;

/** incorrect = genuine error; acceptable = correct but non-standard/variant; optional = style improvement. */
export const IssueKindSchema = z.enum(['incorrect', 'acceptable', 'optional']);

export const EvaluationIssueSchema = z.object({
  /** Offsets into the confirmed transcript (UTF-16, end exclusive). null ⇒ `unlocated`. */
  start: z.number().int().min(0).nullable(),
  end: z.number().int().min(0).nullable(),
  observed: z.string(),
  suggested: z.string(),
  category: FreeIssueCategorySchema,
  kind: IssueKindSchema,
  severity: z.enum(['major', 'minor']),
  explanationRu: z.string().min(1),
  confidence: ConfidenceSchema,
  /** Set by the server when offsets could not be verified. */
  located: z.enum(['exact', 'relocated', 'unlocated']).default('exact'),
});
export type EvaluationIssue = z.infer<typeof EvaluationIssueSchema>;

export const RubricScoreSchema = z.object({
  criterion: z.string(),
  score: z.number().min(0),
  max: z.number().positive(),
  evidence: z.array(z.string()).default([]),
  commentRu: z.string().default(''),
});

export const EvaluationResultSchema = z.object({
  schemaVersion: z.literal(1),
  language: z.literal('el'),
  mode: z.enum(['free', 'exam']),
  /** Minimal A2-appropriate correction of the learner text (not a rewrite). */
  correctedText: z.string(),
  issues: z.array(EvaluationIssueSchema),
  rubric: z.array(RubricScoreSchema),
  overall: z.object({
    score: z.number().min(0),
    max: z.number().positive(),
    label: z.string(),
  }),
  /** Task coverage per required point; empty when there was no prompt. */
  taskCoverage: z
    .array(z.object({ point: z.string(), covered: z.enum(['yes', 'partly', 'no']), evidence: z.string().nullable() }))
    .default([]),
  summaryRu: z.string(),
  strengthsRu: z.array(z.string()).default([]),
  wordCount: z.number().int().min(0),
  limitations: z.array(z.string()).default([]),
});
export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

export const RubricCriterionSchema = z.object({
  key: z.string(),
  labelRu: z.string(),
  labelEn: z.string(),
  max: z.number().positive(),
  descriptionRu: z.string(),
});
export type RubricCriterion = z.infer<typeof RubricCriterionSchema>;

export const RubricSchema = z.object({
  version: z.string(),
  label: z.string(),
  criteria: z.array(RubricCriterionSchema).min(1),
});
export type Rubric = z.infer<typeof RubricSchema>;

/** Default practice rubric — explicitly NOT an official exam rubric. */
export const PRACTICE_RUBRIC_A2: Rubric = {
  version: 'practice-a2-v1',
  label: 'Учебная шкала A2 (не официальная)',
  criteria: [
    {
      key: 'task',
      labelRu: 'Выполнение задания',
      labelEn: 'Task fulfilment',
      max: 10,
      descriptionRu: 'Раскрыты все пункты задания, соблюдены жанр и объём.',
    },
    {
      key: 'organisation',
      labelRu: 'Структура и связность',
      labelEn: 'Organisation',
      max: 10,
      descriptionRu: 'Обращение, абзацы, прощание; связки между предложениями.',
    },
    {
      key: 'vocabulary',
      labelRu: 'Лексика',
      labelEn: 'Vocabulary',
      max: 10,
      descriptionRu: 'Достаточный и уместный словарь уровня A2.',
    },
    {
      key: 'grammar',
      labelRu: 'Грамматика',
      labelEn: 'Grammar',
      max: 10,
      descriptionRu: 'Артикли, согласование, падежи, формы глаголов, порядок слов.',
    },
    {
      key: 'orthography',
      labelRu: 'Орфография и тонос',
      labelEn: 'Spelling & accents',
      max: 10,
      descriptionRu: 'Правописание, тонос, конечная сигма, пунктуация.',
    },
  ],
};

/** Free writing without a prompt: task fulfilment is not scored (spec §5.3). */
export const PRACTICE_RUBRIC_A2_NO_PROMPT: Rubric = {
  ...PRACTICE_RUBRIC_A2,
  version: 'practice-a2-noprompt-v1',
  criteria: PRACTICE_RUBRIC_A2.criteria.filter((c) => c.key !== 'task'),
};
